import { useCallback, useEffect, useState } from 'react';
import {
  Brain, Check, ChevronDown, ChevronUp, Loader2, Pencil, Plus, ShieldCheck,
  Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';

import api from '@/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';


const CATEGORY_LABELS = {
  workflow: 'Рабочий процесс',
  communication: 'Общение',
  schedule: 'Расписание',
  reporting: 'Отчёты',
};

const STATUS_LABELS = {
  processing: 'Обрабатывается',
  completed: 'Выполнено',
  awaiting_confirmation: 'Ждёт подтверждения',
  cancelled: 'Отменено',
  failed: 'Ошибка',
  partial: 'Выполнено частично',
};

const ROLE_LABELS = { owner: 'Владелец', admin: 'Администратор', doctor: 'Специалист' };

const EMPTY_FORM = { category: 'workflow', title: '', value: '' };


const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};


function MemoryForm({ initial = EMPTY_FORM, busy, submitLabel, onSubmit, onCancel }) {
  const [form, setForm] = useState(initial);

  useEffect(() => setForm(initial), [initial]);

  const submit = (event) => {
    event.preventDefault();
    onSubmit(form);
  };

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[170px_1fr]">
      <label className="text-xs font-medium" style={{ color: 'var(--app-text-secondary)' }}>
        Категория
        <select
          value={form.category}
          onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
          className="mt-1.5 h-10 w-full rounded-lg border bg-transparent px-3 text-sm outline-none focus:ring-2"
          style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)', background: 'var(--app-card)' }}
        >
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </label>
      <label className="text-xs font-medium" style={{ color: 'var(--app-text-secondary)' }}>
        Название
        <input
          value={form.title}
          onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
          maxLength={80}
          required
          placeholder="Например, формат утреннего обзора"
          className="mt-1.5 h-10 w-full rounded-lg border bg-transparent px-3 text-sm outline-none focus:ring-2"
          style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)', background: 'var(--app-card)' }}
        />
      </label>
      <label className="text-xs font-medium sm:col-span-2" style={{ color: 'var(--app-text-secondary)' }}>
        Что учитывать
        <textarea
          value={form.value}
          onChange={(event) => setForm((current) => ({ ...current, value: event.target.value }))}
          rows={2}
          maxLength={500}
          required
          placeholder="Только рабочее предпочтение, без сведений о клиентах"
          className="mt-1.5 w-full resize-y rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none focus:ring-2"
          style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)', background: 'var(--app-card)' }}
        />
      </label>
      <div className="flex gap-2 sm:col-span-2">
        <Button size="sm" type="submit" disabled={busy || !form.title.trim() || !form.value.trim()} className="gap-1.5">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {submitLabel}
        </Button>
        {onCancel && (
          <Button size="sm" type="button" variant="ghost" onClick={onCancel} disabled={busy} className="gap-1.5">
            <X size={14} /> Отменить
          </Button>
        )}
      </div>
    </form>
  );
}


export function MemoryPanel() {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [clearReady, setClearReady] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/assistant/memory');
      setMemories(Array.isArray(data.memories) ? data.memories : []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось загрузить память ассистента');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    setSaving(true);
    try {
      await api.post('/assistant/memory', form);
      toast.success('Настройка сохранена');
      setAdding(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось сохранить настройку');
    } finally {
      setSaving(false);
    }
  };

  const update = async (memoryId, form) => {
    setSaving(true);
    try {
      await api.put(`/assistant/memory/${memoryId}`, form);
      toast.success('Настройка обновлена');
      setEditingId('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось обновить настройку');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (memoryId) => {
    setSaving(true);
    try {
      await api.delete(`/assistant/memory/${memoryId}`);
      toast.success('Настройка удалена');
      setDeletingId('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось удалить настройку');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.delete('/assistant/memory');
      toast.success('Память очищена');
      setClearReady(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось очистить память');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="memory-title">
      <div className="flex flex-col gap-4 border-b pb-5 sm:flex-row sm:items-start sm:justify-between" style={{ borderColor: 'var(--app-border)' }}>
        <div className="max-w-xl">
          <div className="flex items-center gap-2">
            <Brain size={19} style={{ color: 'var(--app-accent)' }} />
            <h2 id="memory-title" className="text-lg font-semibold" style={{ color: 'var(--app-text)' }}>Память ассистента</h2>
          </div>
          <p className="mt-2 text-sm leading-6" style={{ color: 'var(--app-text-secondary)' }}>
            Здесь хранятся только ваши подтверждённые рабочие предпочтения. Они действуют на сайте и в Telegram.
          </p>
          <p className="mt-1 text-xs leading-5" style={{ color: 'var(--app-text-muted)' }}>
            Не добавляйте сведения о клиентах и содержание консультаций.
          </p>
        </div>
        <Button size="sm" onClick={() => setAdding((value) => !value)} className="gap-1.5 self-start">
          {adding ? <X size={14} /> : <Plus size={14} />}
          {adding ? 'Закрыть' : 'Добавить настройку'}
        </Button>
      </div>

      {adding && (
        <div className="border-b py-5" style={{ borderColor: 'var(--app-border)' }}>
          <MemoryForm busy={saving} submitLabel="Сохранить" onSubmit={create} />
        </div>
      )}

      {loading ? (
        <div className="space-y-3 py-6"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
      ) : memories.length === 0 ? (
        <div className="py-12 text-center">
          <Brain size={25} className="mx-auto" style={{ color: 'var(--app-text-muted)' }} />
          <p className="mt-3 text-sm font-medium" style={{ color: 'var(--app-text)' }}>Память пока пуста</p>
          <p className="mt-1 text-xs" style={{ color: 'var(--app-text-muted)' }}>Можно также написать ассистенту: «Запомни, что...»</p>
        </div>
      ) : (
        <div>
          {memories.map((memory) => (
            <article key={memory.id} className="border-b py-5" style={{ borderColor: 'var(--app-border-subtle)' }}>
              {editingId === memory.id ? (
                <MemoryForm
                  initial={{ category: memory.category, title: memory.title, value: memory.value }}
                  busy={saving}
                  submitLabel="Сохранить изменения"
                  onSubmit={(form) => update(memory.id, form)}
                  onCancel={() => setEditingId('')}
                />
              ) : (
                <div className="flex items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold" style={{ color: 'var(--app-text)' }}>{memory.title}</h3>
                      <span className="rounded-full px-2 py-0.5 text-[11px]" style={{ color: 'var(--app-accent)', background: 'var(--app-accent-muted)' }}>
                        {CATEGORY_LABELS[memory.category] || memory.category}
                      </span>
                    </div>
                    <p className="mt-1.5 whitespace-pre-wrap text-sm leading-6" style={{ color: 'var(--app-text-secondary)' }}>{memory.value}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Изменить настройку" onClick={() => setEditingId(memory.id)}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Удалить настройку" onClick={() => setDeletingId(memory.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              )}
              {deletingId === memory.id && editingId !== memory.id && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" style={{ color: 'var(--app-text-secondary)' }}>
                  <span>Удалить эту настройку?</span>
                  <Button size="sm" variant="destructive" onClick={() => remove(memory.id)} disabled={saving}>Удалить</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeletingId('')} disabled={saving}>Оставить</Button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {memories.length > 0 && !loading && (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-5">
          <p className="text-xs" style={{ color: 'var(--app-text-muted)' }}>Сохранено настроек: {memories.length} из 50</p>
          {clearReady ? (
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--app-text-secondary)' }}>
              <span>Удалить все настройки?</span>
              <Button size="sm" variant="destructive" onClick={clear} disabled={saving}>Очистить</Button>
              <Button size="sm" variant="ghost" onClick={() => setClearReady(false)} disabled={saving}>Отмена</Button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setClearReady(true)} className="gap-1.5">
              <Trash2 size={14} /> Очистить память
            </Button>
          )}
        </div>
      )}
    </section>
  );
}


export function AuditPanel() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [expanded, setExpanded] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/assistant/audit', { params: { channel, status, limit: 100 } });
      setEntries(Array.isArray(data.entries) ? data.entries : []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось загрузить журнал');
    } finally {
      setLoading(false);
    }
  }, [channel, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <section aria-labelledby="audit-title">
      <div className="border-b pb-5" style={{ borderColor: 'var(--app-border)' }}>
        <div className="flex items-center gap-2">
          <ShieldCheck size={19} style={{ color: 'var(--app-accent)' }} />
          <h2 id="audit-title" className="text-lg font-semibold" style={{ color: 'var(--app-text)' }}>Журнал ассистента</h2>
        </div>
        <p className="mt-2 max-w-2xl text-sm leading-6" style={{ color: 'var(--app-text-secondary)' }}>
          В журнале видно, кто отправил команду, как ассистент её понял и какие действия действительно выполнил.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <select
            value={channel}
            onChange={(event) => setChannel(event.target.value)}
            aria-label="Канал"
            className="h-9 rounded-lg border px-3 text-xs outline-none focus:ring-2"
            style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)', background: 'var(--app-card)' }}
          >
            <option value="">Все каналы</option><option value="web">Сайт</option><option value="telegram">Telegram</option>
          </select>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Статус"
            className="h-9 rounded-lg border px-3 text-xs outline-none focus:ring-2"
            style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)', background: 'var(--app-card)' }}
          >
            <option value="">Все статусы</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3 py-6"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>
      ) : entries.length === 0 ? (
        <div className="py-12 text-center">
          <ShieldCheck size={25} className="mx-auto" style={{ color: 'var(--app-text-muted)' }} />
          <p className="mt-3 text-sm font-medium" style={{ color: 'var(--app-text)' }}>Записей пока нет</p>
        </div>
      ) : (
        <div>
          {entries.map((entry) => {
            const isOpen = expanded === entry.id;
            const privacyCount = Number(entry.privacy?.replacements || 0);
            return (
              <article key={entry.id} className="border-b py-5" style={{ borderColor: 'var(--app-border-subtle)' }}>
                <button type="button" onClick={() => setExpanded(isOpen ? '' : entry.id)} className="w-full text-left focus-visible:outline-none focus-visible:ring-2">
                  <div className="flex items-start gap-3">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ background: entry.status === 'failed' ? '#ef4444' : 'var(--app-accent)' }} />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]" style={{ color: 'var(--app-text-muted)' }}>
                        <span>{formatDate(entry.created_at)}</span>
                        <span>{entry.channel === 'telegram' ? 'Telegram' : 'Сайт'}</span>
                        <span>{entry.user_name || ROLE_LABELS[entry.user_role] || 'Пользователь'}</span>
                        <span>{STATUS_LABELS[entry.status] || entry.status}</span>
                        {privacyCount > 0 && (
                          <span className="rounded-full px-2 py-0.5" style={{ color: 'var(--app-accent)', background: 'var(--app-accent-muted)' }}>
                            Скрыто данных: {privacyCount}
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-sm font-medium leading-5" style={{ color: 'var(--app-text)' }}>{entry.original_command}</p>
                    </div>
                    {isOpen ? <ChevronUp size={16} style={{ color: 'var(--app-text-muted)' }} /> : <ChevronDown size={16} style={{ color: 'var(--app-text-muted)' }} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="ml-5 mt-4 space-y-4 border-l pl-4" style={{ borderColor: 'var(--app-border)' }}>
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--app-text-muted)' }}>Как понял ассистент</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-6" style={{ color: 'var(--app-text-secondary)' }}>{entry.interpreted_message || 'Трактовка не сохранена'}</p>
                    </div>
                    {privacyCount > 0 && (
                      <div>
                        <p className="text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--app-text-muted)' }}>Защита данных перед внешним ИИ</p>
                        <p className="mt-1 text-sm" style={{ color: 'var(--app-text-secondary)' }}>
                          Исходные значения заменены псевдонимами. Типы: {Object.keys(entry.privacy?.types || {}).join(', ') || 'персональные данные'}.
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-[11px] font-medium uppercase tracking-wide" style={{ color: 'var(--app-text-muted)' }}>Действия</p>
                      {entry.actions.length === 0 ? (
                        <p className="mt-1 text-sm" style={{ color: 'var(--app-text-secondary)' }}>Данные не изменялись</p>
                      ) : (
                        <div className="mt-1 divide-y" style={{ borderColor: 'var(--app-border-subtle)' }}>
                          {entry.actions.map((action) => (
                            <div key={action.id} className="py-2 text-sm">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium" style={{ color: 'var(--app-text)' }}>{action.tool}</span>
                                <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>{STATUS_LABELS[action.status] || action.status}</span>
                                {action.confirmation_status === 'confirmed' && <span className="text-xs" style={{ color: 'var(--app-accent)' }}>Подтверждено</span>}
                              </div>
                              {action.error && <p className="mt-1 text-xs text-red-500">{action.error}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
